import { Container, Divider } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors, textCapitalization } from "utils";

function OriginalPublisherContract() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams.get("search");
  const paramsPage = urlParams.get("page");
  const paramsSize = urlParams.get("limit");
  const paramsCategory = urlParams.get("category");
  const paramsStatus = urlParams.get("status");
  const paramsApprovalStatus = urlParams.get("approval");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    v: 2,
    page: Number(paramsPage) || 1,
    limit: Number(paramsSize) || 10,
    offset: 0,
    search: paramsSearch || "",
    composer: "",
    category: paramsCategory || "",
    status: paramsStatus || "",
    approval: paramsApprovalStatus || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getDataTable = async () => {
    const options = {
      headers,
      params: queryParams,
    };
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/publisher/contract`, options);
      const { data, meta } = res?.data;
      setDataTable(data?.contracts);
      const pageCount = getTotalPage(meta?.total, queryParams?.limit);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const handleDelete = async item => {
    const { category, contract_id } = item;
    const mainContract = category === "main";
    const url = `${hardBaseUrl}/publisher/contract${
      mainContract ? "/composer-main" : ""
    }/${contract_id}`;
    try {
      await axios.delete(url, { headers });
      ModalSuccess("Delete Contract Success").then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  useEffect(() => {
    debounceDataTable();
    return () => {
      debounceDataTable.cancel();
    };
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Contract Composer/Author">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Contract Composer/Author"
          breadcrumbData={breadcrumbData}
        />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container justifyContent="space-between" spacing={1} my={2}>
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              {filterList.map(filter => (
                <Grid item key={filter?.name}>
                  <SelectInput
                    placeholder="None"
                    label={filter?.label}
                    value={queryParams?.[filter?.name]}
                    onChange={e =>
                      handleChangeQueryParams(e?.target?.value, filter?.name)
                    }
                    options={filter?.options}
                    optionKey="value"
                    optionLabel="label"
                    width={filter?.name === "approval" ? 200 : 150}
                  />
                </Grid>
              ))}
              <Grid item>
                <PrimaryButton
                  label="Create Contract"
                  onClick={() => history.push("/admin/kontrak-composer/tambah")}
                  startIcon={<AddIcon />}
                  size="large"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.limit}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "limit")
          }
          isHaveAction
          disabledEdit={item => item.approval_status === "approved"}
          disabledDelete={item => item.approval_status === "approved"}
          handleEdit={item =>
            history.push({
              pathname: `/admin/kontrak-composer/edit/${item.contract_id}`,
              state: { customer: item },
            })
          }
          handleView={item =>
            history.push({
              pathname: `/admin/kontrak-composer/${item.contract_id}`,
              state: { customer: item },
            })
          }
          deleteConfirmation
          deleteConfirmationTitle="Delete Contract"
          deleteConfirmationKey="contract_number"
          handleDelete={item => handleDelete(item)}
        />
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Contract Composer/Author",
    active: true,
  },
];
const categoryList = [
  {
    label: "Main",
    value: "main",
  },
  {
    label: "Song",
    value: "song",
  },
];
const statusList = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "non-active",
  },
];
const approvalStatusList = [
  {
    label: "Approved",
    value: "approved",
    type: "success",
  },
  {
    label: "Rejected",
    value: "rejected",
    type: "danger",
  },
  {
    label: "Waiting for Approval",
    value: "waiting_for_approval",
    type: "warning",
  },
  {
    label: "Waiting for Editing Approval",
    value: "waiting_for_editing_approval",
    type: "warning",
  },
];
const filterList = [
  { label: "Category", name: "category", options: categoryList },
  { label: "Status", name: "status", options: statusList },
  { label: "Approval Status", name: "approval", options: approvalStatusList },
];
const columnTable = [
  {
    name: "contract_number",
    title: "Contract Number",
    componentType: "text",
  },
  {
    name: "category",
    title: "Category",
    componentType: "text",
    renderText: item => textCapitalization(item, "category"),
  },
  {
    name: "songs",
    title: "Song Title",
    renderText: items => {
      const modifiedItems = (items || [])?.map(item => ({
        ...item,
        name: item?.song_title || "-",
      }));
      return <ArrayChip list={modifiedItems} max={3} />;
    },
  },
  {
    name: "all",
    title: "Composer / Author",
    renderText: item => {
      const modifiedItems =
        item?.category === "main"
          ? [item?.composer?.sure_name || "-"]
          : (item?.composers || [])?.map(item => item?.sure_name || "-");
      return <ArrayChip list={modifiedItems} max={3} />;
    },
  },
  {
    name: "start_date",
    title: "Start Date",
    componentType: "text",
    renderText: item =>
      item ? formatDate(moment(item, "DD-MM-YYYY HH:mm")) : "-",
  },
  {
    name: "end_date",
    title: "End Date",
    componentType: "text",
    renderText: item =>
      item ? formatDate(moment(item, "DD-MM-YYYY HH:mm")) : "-",
  },
  {
    name: "is_active_flag",
    title: "Contract Status",
    componentType: "text",
    renderText: status => (
      <StatusChip
        type={status && "success"}
        label={status ? "Active" : "Inactive"}
      />
    ),
  },
  {
    name: "updated_at",
    title: "Last Update",
    componentType: "text",
    renderText: item => (item ? formatDate(moment(item, "DD-MMM-YYYY")) : "-"),
  },
  {
    name: "approval_status",
    title: "Approval Status",
    componentType: "text",
    renderText: status => {
      const selectedStatus = approvalStatusList?.find(
        item => item.value === status
      );
      return (
        <StatusChip
          label={selectedStatus?.label || "-"}
          type={selectedStatus?.type || "-"}
        />
      );
    },
  },
];
export default OriginalPublisherContract;
